import React from 'react';
import PropTypes from 'prop-types';
import {
    withStyles,
    Grid,
    Typography,
    Button,
} from '@material-ui/core';
import Translator from '%app/js/common/services/translator';
import Link from '%app/js/common/components/material/Link';
import resolveNeutralArtworkStyle from '%evp_theme/brandbook/appearance/resolveNeutralArtworkStyle';
import { EMPTY_STATE_SHEET } from '%evp_theme/brandbook/material/config/styleSheetNames';

const style = theme => ({
    root: {},
    headline: {},
    subheading: {},
    image: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5),
        width: 160,
        ...resolveNeutralArtworkStyle(theme),
    },
    buttonGroup: {
        marginTop: theme.spacing(5),
        textAlign: 'center',
    },
    button: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
});


const resolveButton = (buttonOptions, className) => {
    const {
        buttonText,
        buttonAction,
        asRouteLink,
        children,
        createButtonId,
    } = buttonOptions;

    if (children !== null) {
        return React.cloneElement(<div>{children}</div>, { className });
    }

    if (asRouteLink) {
        return (
            <Link to={buttonAction}>
                <Button variant="contained" className={className} id={createButtonId}>
                    {Translator.trans(buttonText)}
                </Button>
            </Link>
        );
    }

    if (buttonText !== null && buttonAction !== null) {
        if (typeof buttonAction === 'function') {
            return (
                <Button variant="contained" className={className} onClick={buttonAction} id={createButtonId}>
                    {Translator.trans(buttonText)}
                </Button>
            );
        }
        return (
            <Button variant="contained" component="a" className={className} href={buttonAction} id={createButtonId}>
                {Translator.trans(buttonText)}
            </Button>
        );
    }

    return null;
};

function EmptyState({
    classes,
    image,
    headline,
    headlineComponent,
    subheading,
    buttonText = null,
    buttonAction = null,
    asRouteLink = false,
    children = null,
    createButtonId = 'create-entry-button',
    secondaryButtonText = null,
    secondaryButtonAction = null,
    secondaryButtonProps = null,
    disclaimer = null,
}) {
    return (
        <Grid container justify="center" alignItems="center" direction="column" className={classes.root}>
            <img className={classes.image} src={image} />
            <Typography variant="h5" component={headlineComponent} className={classes.headline}>
                {Translator.trans(headline)}
            </Typography>
            <Typography variant="subtitle1" align="center" className={classes.subheading}>
                {Translator.trans(subheading)}
            </Typography>
            {disclaimer}
            <div className={classes.buttonGroup}>
                {(secondaryButtonAction || secondaryButtonProps) && secondaryButtonText && (
                    <Button
                        variant="contained"
                        color="default"
                        className={classes.button}
                        onClick={secondaryButtonAction}
                        {...(secondaryButtonProps || {})}
                    >
                        {Translator.trans(secondaryButtonText)}
                    </Button>
                )}
                {resolveButton({
                    buttonText,
                    buttonAction,
                    asRouteLink,
                    children,
                    createButtonId,
                }, classes.button)}
            </div>
        </Grid>
    );
}

EmptyState.propTypes = {
    classes: PropTypes.shape({
        root: PropTypes.string,
        headline: PropTypes.string,
        subheading: PropTypes.string,
        image: PropTypes.string,
        buttonGroup: PropTypes.string,
        button: PropTypes.string,
    }).isRequired,
    image: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    headlineComponent: PropTypes.elementType,
    subheading: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    secondaryButtonText: PropTypes.string,
    asRouteLink: PropTypes.bool,
    buttonAction: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    secondaryButtonAction: PropTypes.func,
    children: PropTypes.node,
    createButtonId: PropTypes.string,
    secondaryButtonProps: PropTypes.objectOf(PropTypes.any),
    disclaimer: PropTypes.node,
};

export default React.memo(withStyles(style, { name: EMPTY_STATE_SHEET })(EmptyState));
